import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import excelIcon from '@/assets/icon/excel-icon-download.svg';
import { cctvStreamService, fetchCctvList } from '@/domains/traffic-monitoring/apis/cctv-stream';
import BackLink from '@/domains/traffic-monitoring/components/BackLink/BackLink';
import CardInOut from '@/domains/traffic-monitoring/components/CardInOut/CardInOut';
import CardVolumeVehicle from '@/domains/traffic-monitoring/components/CardVolumeVehicle/CardVolumeVehicle';
import ModalExportExcel from '@/domains/traffic-monitoring/components/ModalExportExcel/ModalExportExcel';
import ProgressCard from '@/domains/traffic-monitoring/components/ProgressCard/ProgressCard';
import TimeFilter from '@/domains/traffic-monitoring/components/TimeFilter/TimeFilter';
import VideoCard from '@/domains/traffic-monitoring/components/VideoCard/VideoCard';
import { useAnomalyTrend } from '@/domains/traffic-monitoring/hooks/useAnomalyTrend';
import { useCctvDetail } from '@/domains/traffic-monitoring/hooks/useCctvList';
import { useTrafficExport } from '@/domains/traffic-monitoring/hooks/useTrafficExport';
import { useTrafficVolumeByVehicle } from '@/domains/traffic-monitoring/hooks/useTrafficVolume';
import { useVehicleInOut } from '@/domains/traffic-monitoring/hooks/useVehicleInOut';
import Header from '@/shared/components/layout/Header/Header';
import Button from '@/shared/components/ui/Button';
import LineChart, { type SeriesItem } from '@/shared/components/ui/LineChart/LineChart';
import { useTrafficCongestion } from '../../hooks/useTrafficCongestion';

const options = ['1h', '6h', '12h', '24h'] as const;

const CCTVDetailDashboard = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // ?cctvId= is the canonical source (Event List links, shareable URLs,
  // page refresh); router state remains as fallback for older callers.
  const rawCctvId = searchParams.get('cctvId');
  const parsedCctvId = rawCctvId != null ? Number(rawCctvId) : Number.NaN;
  const cctvId = Number.isFinite(parsedCctvId)
    ? parsedCctvId
    : ((location.state as { cctvId?: number }) || null)?.cctvId;
  const [filterAbnormal, setFilterAbnormal] = useState('1h');
  const navigate = useNavigate();

  // Get CCTV-Detail Data
  const { cctv, isLoading } = useCctvDetail(cctvId);

  // Each cctv live numbers - these pools every 2s via crossroad constant
  const { buckets: vehicleBuckets } = useTrafficVolumeByVehicle(
    cctvId != null ? { cctv_id: cctvId } : {}
  );

  // PSC_PC0203001_BE_004 - this camera's incoming/outgoing volume + avg speed.
  // `both` reads either direction keyword: the City Gateway cameras (cctv
  // 21/23/27) publish as into_out_to_grobogan and leave `direction` null.
  const { incoming, outgoing } = useVehicleInOut({
    ...(cctvId != null ? { cctv_id: cctvId } : {}),
    direction_field: 'both',
  });
  const { congestionPct, peakHour, peakVolume } = useTrafficCongestion(
    cctvId != null ? { cctv_id: cctvId } : {}
  );

  // Anomaly Trend drill-down (30s pool)
  const { xAxisData: abnormalX, series: abnormalSeries } = useAnomalyTrend({
    filter: filterAbnormal,
    cctvId,
  });

  // PSC_PC0204002_BE_001 - Excel export of this camera's daily vehicle and
  // event counts over a range picked in the dialog.
  const [isExportOpen, setIsExportOpen] = useState(false);
  const {
    runExport,
    isExporting,
    errorMessage: exportError,
    clearError,
  } = useTrafficExport('cctv');

  const closeExport = () => {
    clearError();
    setIsExportOpen(false);
  };

  // A finished export closes the dialog; a failed one leaves it open so the
  // message sits next to the range that produced it.
  const handleExport = async (startDate: string, endDate: string, interval: string) => {
    if (cctvId == null) return;
    const succeeded = await runExport(startDate, endDate, { cctv_id: String(cctvId), interval });
    if (succeeded) setIsExportOpen(false);
  };

  const eventTypeTranslationKeys: Record<string, string> = {
    Overspeed: 'overspeed',
    Jaywalk: 'jaywalk',
    'Contra Flow': 'contraflow',
    'Sudden Stop': 'suddenstop',
  };

  const translatedAbnormalSeries = abnormalSeries.map((item) => {
    const translationKey = eventTypeTranslationKeys[item.name];

    return {
      ...item,
      name: translationKey ? t(`traffic>event type>${translationKey}`) : item.name,
    };
  });

  const chartBottom = i18n.language === 'eng' ? '60px' : '60px';

  const DEVICE_STATUS_TO_TYPE: Record<string, 'online' | 'offline' | 'maintenance'> = {
    DEV_ST_ON: 'online',
    DEV_ST_OFF: 'offline',
    DEV_ST_MTN: 'maintenance',
  };

  // console.log("dataok", data)
  return (
    <section className="_cctvDashboard-detail-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className="_cctvDashboard-detail-screen p-3 w-full h-[calc(95vh-15px)] grid grid-cols-[2.2fr_1fr] gap-3 overflow-hidden ">
        <div className="p-6 w-full flex flex-col gap-3 overflow-auto h-full border gap-3 rounded-[12px] bg-db-card bg-db-border">
          <div className="flex flex-row gap-3 justify-between items-center">
            <BackLink
              label={t('cm > back')}
              onclick={() => navigate(-1)}
              subLabel={isLoading ? 'Loading...' : cctv?.name || ''}
            />
            <Button
              customButton="btn-export shrink-0"
              disabled={cctvId == null}
              onClick={() => setIsExportOpen(true)}
            >
              <img src={excelIcon} alt="" aria-hidden="true" />
              {t('cmn>master>export excel', 'Export Excel')}
            </Button>
          </div>
          <div className="w-full h-full flex-7 overflow-hidden">
            {cctv && (
              <VideoCard
                className="w-full"
                showLocation
                typeVideo={DEVICE_STATUS_TO_TYPE[cctv.device_status] ?? 'maintenance'}
                streamUrl={cctvStreamService.getHlsUrl(cctv)}
                isHighRes
              />
            )}
          </div>
          <div className="grid grid-cols-4 gap-3 items-start flex-1 ">
            {vehicleBuckets.map((bucket) => (
              <CardVolumeVehicle
                key={bucket.title}
                title={t(`traffic>vehicle>${bucket.title}`)}
                totalVehicle={bucket.total}
                typeArrow={bucket.typeArrow}
                percentage={bucket.changePct}
              />
            ))}
          </div>
        </div>

        <div className="_cctvDashboard-detail-screen w-full flex flex-col gap-3 overflow-auto h-[calc(100vh-80px)]  ">
          <div className="border p-6 rounded-[12px] bg-db-card bg-db-border  min-h-[300px] flex-1">
            <div className="w-full items-center flex gap-3 justify-between">
              <span className="title1bold capitalize">{t('cmn>master>abnormality trends')}</span>
              <TimeFilter options={options} value={filterAbnormal} onChange={setFilterAbnormal} />
            </div>
            <div className="w-full h-[calc(100%-30px)] ">
              <LineChart
                xAxisData={abnormalX}
                series={translatedAbnormalSeries}
                bottom={chartBottom}
              />
            </div>
          </div>
          <div className="border p-6 rounded-[12px] bg-db-card bg-db-border  h-fit ">
            <div className="w-full h-fit flex flex-col gap-3">
              <ProgressCard
                title={t('traffic>master>overall congestion level')}
                value={congestionPct}
                peakHour={peakHour}
                peakVolume={peakVolume}
              />
              <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] items-center gap-3  ">
                <CardInOut
                  title={t('traffic>master>incoming')}
                  totalVolume={incoming.volume}
                  totalSpeed={incoming.speed}
                />
                <CardInOut
                  title={t('traffic>master>outgoing')}
                  typeCard="outgoing"
                  totalVolume={outgoing.volume}
                  totalSpeed={outgoing.speed}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalExportExcel
        isOpen={isExportOpen}
        onClose={closeExport}
        title={t('traffic>export>export traffic chart by cctv', 'Export Traffic Chart by CCTV')}
        fieldPrefix="cctv"
        isExporting={isExporting}
        errorMessage={exportError}
        onExport={handleExport}
      />
    </section>
  );
};

export default CCTVDetailDashboard;
